import { useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getAttribution } from "@/lib/attribution";
import { CONSENT_TEXT, STATES, JOURNEYS, BONUSES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full bg-white/[0.04] border border-white/10 px-4 py-3 text-sm text-[#F0ECE4] placeholder:text-white/25 focus:border-[#D9622B] focus:outline-none transition-colors";

export default function RegistrationForm({ variant = "hero", showBonuses = true }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", state: "", journey: "" });
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error("Please check the consent box to register."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/ghl-lead`, {
        ...form,
        journey: form.journey || null,
        consent: true,
        consentTimestamp: new Date().toISOString(),
        consentText: CONSENT_TEXT,
        ...getAttribution(),
      });
      navigate("/thank-you");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Please check your details and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass-card p-6 sm:p-8" data-testid={`reg-card-${variant}`}>
      {showBonuses && (
        <>
          <p className="font-mono text-[10px] tracking-[0.3em] text-[#D9622B]">DON'T MISS OUT</p>
          <h3 className="mt-2 text-lg font-bold text-[#F0ECE4]">When You Register, You'll Receive:</h3>
          <ul className="mt-4 space-y-2.5">
            {BONUSES.map((b) => (
              <li key={b} className="flex gap-2.5 text-[13px] text-white/70 leading-snug">
                <span className="text-[#D9622B] font-bold shrink-0">✓</span>{b}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-[22px] font-black text-[#D9622B]">FREE!</p>
        </>
      )}
      <form onSubmit={submit} className={`${showBonuses ? "mt-5" : ""} space-y-3`}>
        <div className="grid grid-cols-2 gap-3">
          <input data-testid={`input-firstname-${variant}`} className={inputCls} placeholder="First name" required value={form.firstName} onChange={set("firstName")} />
          <input data-testid={`input-lastname-${variant}`} className={inputCls} placeholder="Last name" required value={form.lastName} onChange={set("lastName")} />
        </div>
        <input data-testid={`input-email-${variant}`} className={inputCls} type="email" placeholder="Email address" required value={form.email} onChange={set("email")} />
        <input data-testid={`input-phone-${variant}`} className={inputCls} type="tel" placeholder="Mobile phone" required value={form.phone} onChange={set("phone")} />
        <select data-testid={`input-state-${variant}`} className={inputCls} required value={form.state} onChange={set("state")}>
          <option value="" disabled>State</option>
          {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid={`input-journey-${variant}`} className={inputCls} value={form.journey} onChange={set("journey")}>
          <option value="">Where are you in your homebuying journey? (optional)</option>
          {JOURNEYS.map((j) => <option key={j} value={j}>{j}</option>)}
        </select>
        <label className="flex items-start gap-2.5 cursor-pointer">
          <input data-testid={`input-consent-${variant}`} type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-[#D9622B]" />
          <span className="text-[10px] leading-relaxed text-white/35">
            {CONSENT_TEXT}{" "}
            <Link to="/privacy" className="underline hover:text-white/60">Privacy Policy</Link> · <Link to="/terms" className="underline hover:text-white/60">Terms</Link>
          </span>
        </label>
        <button
          data-testid={`submit-btn-${variant}`}
          disabled={busy}
          className="w-full py-4 btn-primary text-black font-black text-sm tracking-[0.1em] disabled:opacity-60"
        >
          {busy ? "RESERVING..." : "✓ RESERVE MY FREE SEAT"}
        </button>
        <p className="text-center text-[10px] text-white/25">Free educational event · No obligation · No hard credit pull required to attend</p>
      </form>
    </div>
  );
}
